import React, { useCallback, useEffect, useState, useRef } from "react";
import { Box, Grid, } from "@mui/material";
import GameEventButtons from "./gameevents/GameEventButtons";
import PostsManager from "./posts/PostsManager";
import { DirectProvider } from "./DirectContext.js";
import MatchPreview from "./preview/MatchPreview.js";
import './matchview.css'
import { useDispatch } from "react-redux";
import { confirmActionNotification } from '../../../../actions/notifications.js';
import { deleteArticle } from "../../../../actions/article.js";
import { deleteSurvey } from "../../../../actions/sondage.js";
import { deleteGameFact } from "../../../../actions/gameFact.js";
import { MEDIA_ARTICLE, MEDIA_NOTATION, MEDIA_QUESTIONBOX, MEDIA_QUIZZ, MEDIA_SURVEY } from "./data/constants.js";
import MatchHeader from "./header/MatchHeader.js";
import FeuilleMatchCreation from "../../../../dsasComponents/feuillematch/FeuilleMatchCreation.js";
import { InfoMatchCreation } from "../../../../components/Live/inlive/MatchInLive.js";
import { MatchEventFactory } from "./data/factories/MatchEventFactory.js";
import { deleteNotation } from "../../../../actions/notation.js";
import { deleteQuizz } from "../../../../actions/quizz.js";
import { useSubscription } from "../../../hooks/useSubscription.js";
import { deleteQuestionBox } from "../../../../actions/questionBox.js";
import DsasButton from "../../../components/DsasButton/DsasButton.js";
import SwitchToggle from "./components/SwitchToggle.js";

const VIEW = {
  EVENTS : 'events',
  LINEUP : 'lineup',
  INFO : 'info'
}

/**
 * MatchView component displays and manages the main view for a sports match, including game events,
 * lineup, and match information. It provides interfaces for creating, editing, and deleting match-related posts
 * such as articles, surveys, notations, and quizzes. The component also handles user interactions for switching
 * between different match views and manages the state of posts and active post dialogs.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.match - The match object containing match data and metadata.
 * @param {Function} props.loadMore - Callback function to load more data (e.g., for infinite scroll/history).
 *
 * @returns {JSX.Element} The rendered MatchView component.
 *
 * @example
 * <MatchView match={matchData} loadMore={handleLoadMore} />
 */
export const MatchView = ({ match, loadMore }) => {

  const postsManagerRef = useRef(null); // to set anchor & go up in gamefact edit mode (mobile device view)
  const gamefactsButtonsRef = useRef(null); // to set anchor & go up in gamefact edit mode (mobile device view)

  const dispatch = useDispatch()

  const { checkSubscription } = useSubscription(match)

  const [isAutomaticCommentary, setIsAutomaticCommentary] = useState(true);
  const [isEnableTrigger, setIsEnableTrigger] = useState(checkSubscription(1));

  const [posts, setPosts] = useState([]);
  const [activePost, setActivePost] = useState(null);

  const [displayView, setDisplayView] = useState(VIEW.EVENTS)
  const [shouldScrollToGamefacts, setShouldScrollToGamefacts] = useState(false);

  

  useEffect(() => {
    setPosts([])
  }, [match._id])

  const scrollToTabs = () => {
    
    if (postsManagerRef.current) {
      postsManagerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

  }

  const scrollToGamefactsButtons = () => {
    
    if (gamefactsButtonsRef.current) {
      gamefactsButtonsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

  }

    useEffect(() => {
    if (displayView === VIEW.EVENTS && shouldScrollToGamefacts) {
      const timeout = setTimeout(() => {
        scrollToGamefactsButtons();
        setShouldScrollToGamefacts(false);
      }, 100); // petit délai pour laisser le temps au DOM de s'afficher
      return () => clearTimeout(timeout);
    }
  }, [displayView, shouldScrollToGamefacts]);

  const handleEventClick = useCallback((event, showInPopup = false, callback) => {

    scrollToTabs();

    setPosts((prevPosts) => {

      
        const eventDto = MatchEventFactory.create({...event, isAutomaticCommentary, isEnableTrigger}) 

        // Définir timer et period grace au match
        eventDto.initTimerPeriod(match)

        console.log('match period:', match.period, ' timer:', match.timer);

        console.log('eventDto after initTimerPeriod:', eventDto);

        const newPost = {
            id: Date.now(), 
            //label: event.label, 
            event : eventDto,
            showInPopup,
            onValidate : callback
        };
        const updatedPosts = [...prevPosts, newPost];
    
        setActivePost(newPost.id);
        return updatedPosts; 
      });
  }, [isAutomaticCommentary, isEnableTrigger, match]);

  const handleEventClickWithCallback = useCallback((event, callback) => {
    handleEventClick(event, true, callback)

  }, [handleEventClick])

  const handleEventEdit = (event) => {
    // scroll to postsManagerRef
    scrollToTabs();

    setPosts((prevPosts) => {
      const newPost = {
          id: Date.now(), 
          //title: event.title ?? event._id, 
          event : MatchEventFactory.create({...event, isAutomaticCommentary}) 
      };

      const updatedPosts = [...prevPosts, newPost];
  
      setActivePost(newPost.id);
      return updatedPosts; 
    });
  }

  const handleViewObserver = (item) => {

    //TODO add to setPost directly everywhere?
    scrollToTabs();

    // L'idee ici est d'ouvrir un post qui permet d'observer les questions
    setPosts((prevPosts) => {
      const newPost = {
          id: Date.now(), 
          //title: event.title ?? event._id, 
          observer : item,
          action : (pseudo, question) => {
            const event = {
              tab: "Réponse à "+pseudo,
              subscription : 1,
              type : MEDIA_ARTICLE,
              content : `<p>${question} ? <strong>${pseudo}</strong> </p><p><br></p><p><br></p><p><br></p>`
            }
            handleEventClick(event)
          }
      };

      const updatedPosts = [...prevPosts, newPost];

      setActivePost(newPost.id);
      return updatedPosts;
    });
    
  }

  const handleEventDelete = (event) => {

    dispatch(confirmActionNotification("Êtes-vous sûr de vouloir supprimer ce post ?", () => {

    switch(event.type){
        case MEDIA_NOTATION :
          dispatch(deleteNotation(event._id))
        break
        case MEDIA_ARTICLE :
          dispatch(deleteArticle(event._id))
        break
        case MEDIA_SURVEY :
          dispatch(deleteSurvey(event._id))
        break
        case MEDIA_QUIZZ :
          dispatch(deleteQuizz(event._id))
        break
        case MEDIA_QUESTIONBOX :
          dispatch(deleteQuestionBox(event._id))
        break
        default :
          dispatch(deleteGameFact(event._id))
      }
    }));
  }

  return (
    <>
    <DirectProvider directEvent={match}>
      <Grid container>
        <Grid item xs={12} sm={12} md={8} lg={9} >
          <Box className='medium-padding'>
            {/*TEAMS & STATUS*/}
            <MatchHeader handleEventClick={handleEventClickWithCallback}/>

            <Box className='large-separator'></Box>


            {/*GAMEFACTS SECTION*/}
            {/*NAVIGATION BUTTONS*/}
            {/*TODO MOBILE DISPLAY WIDTH */}
            <Box className='centered-medium-wrapper'>
              <DsasButton 
                label="Direct" 
                variant="primary" 
                onClick={() => setDisplayView(VIEW.EVENTS)} 
                focused={displayView === VIEW.EVENTS}/>
              {checkSubscription(2) && 
                <DsasButton 
                  label="Composition" 
                  variant="primary" 
                  onClick={() => setDisplayView(VIEW.LINEUP)} 
                  focused={displayView === VIEW.LINEUP}/>
                }
              {checkSubscription(4) && 
              <DsasButton label="Information" 
                variant="primary" 
                onClick={() => setDisplayView(VIEW.INFO)} 
                focused={displayView === VIEW.INFO}/>
              }
            </Box>

            {/*NAVIGATION PAGES*/}
            {/*LIVE GAME - GAMEFACTS*/}
            {displayView === VIEW.EVENTS && <>
              <SwitchToggle
                label="Commentaires automatiques"
                checked={isAutomaticCommentary}
                onSwitch={setIsAutomaticCommentary}
              />
              {checkSubscription(1) && <SwitchToggle
                label="Sondages automatiques"
                checked={isEnableTrigger}
                onSwitch={setIsEnableTrigger}
              />}
              {/* SCROLL ANCHOR GFs*/}
              <div ref={gamefactsButtonsRef} className="live-gf-scroll-target"/>
              <GameEventButtons discipline={match.discipline} onEventClick={handleEventClick} />
              {/* SCROLL ANCHOR POSTS*/}
              <div ref={postsManagerRef} className="live-gf-scroll-target"/>
              <PostsManager 
                posts={posts} 
                setPosts={setPosts} 
                activePost={activePost} 
                setActivePost={setActivePost} 

                isAutomaticCommentary={isAutomaticCommentary}

                submitCallback={scrollToGamefactsButtons}

              />
            </>
            }

            {/*MATCH LINEUP*/}
            {displayView === VIEW.LINEUP && <>
              <FeuilleMatchCreation match={match} close={() => {
                setDisplayView(VIEW.EVENTS);
                setShouldScrollToGamefacts(true);
              }}/>
            </>
            }

            {/*STADIUM / AREA INFOS*/}
            {displayView === VIEW.INFO && <>
              <InfoMatchCreation match={match} close={() => setDisplayView(VIEW.EVENTS)}/>
            </>
            }
            </Box>
          </Grid>
        

          {/*LIVE PREVIEW / HISTORY */}
          <Grid item xs={12} sm={12} md={4} lg={3} >
             <Box className='bordered-wrapper small-padding' sx={{minHeight : '60rem', marginTop: '0.5rem'}}>
                <MatchPreview loadMore={loadMore} handleEventEdit={handleEventEdit} handleEventDelete={handleEventDelete} handleViewObserver={handleViewObserver}/>
              </Box>
          </Grid>
        </Grid>
      </DirectProvider>
    </>
  );
};